// -----------------------------------------------------------------------------------------------------
// @ AUTH UTILITIES
//
// Methods are derivations of the Auth0 Angular-JWT helper service methods
// https://github.com/auth0/angular2-jwt
// -----------------------------------------------------------------------------------------------------

export class AuthUtils
{
    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Check if token has JWT format
     */
    static isValidJwt(token?: string | null): boolean
    {
        return !!token && token.split('.').length === 3;
    }

    /**
     * Is token expired?
     *
     * @param token
     * @param offsetSeconds
     */
    static isTokenExpired(token: string, offsetSeconds?: number): boolean
    {
        if ( !this.isValidJwt(token) )
        {
            return true;
        }

        const date = this._getTokenExpirationDate(token);

        offsetSeconds = offsetSeconds || 0;

        if ( date === null )
        {
            return true;
        }

        return !(date.valueOf() > new Date().valueOf() + offsetSeconds * 1000);
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    private static _b64decode(str: string): string
    {
        const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/=';
        let output = '';

        str = String(str).replace(/=+$/, '');

        if ( str.length % 4 === 1 )
        {
            return '';
        }

        /* eslint-disable */
        for (
            let bc = 0, bs: any, buffer: any, idx = 0;
            (buffer = str.charAt(idx++));
            ~buffer &&
            (
                (bs = bc % 4 ? bs * 64 + buffer : buffer),
                bc++ % 4
            )
                ? (output += String.fromCharCode(255 & (bs >> ((-2 * bc) & 6))))
                : 0
        )
        {
            buffer = chars.indexOf(buffer);
        }
        /* eslint-enable */

        return output;
    }

    private static _b64DecodeUnicode(str: string): string
    {
        try
        {
            return decodeURIComponent(
                Array.prototype.map
                    .call(this._b64decode(str), (c: any) =>
                        '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
                    )
                    .join('')
            );
        }
        catch
        {
            return '';
        }
    }

    private static _urlBase64Decode(str: string): string
    {
        let output = str.replace(/-/g, '+').replace(/_/g, '/');

        switch ( output.length % 4 )
        {
            case 0: break;
            case 2: output += '=='; break;
            case 3: output += '='; break;
            default: return '';
        }

        return this._b64DecodeUnicode(output);
    }

    private static _decodeToken(token: string): any | null
    {
        if ( !this.isValidJwt(token) )
        {
            return null;
        }

        try
        {
            const parts = token.split('.');
            const decoded = this._urlBase64Decode(parts[1]);

            return decoded ? JSON.parse(decoded) : null;
        }
        catch
        {
            return null;
        }
    }

    static _getTokenExpirationDate(token: string): Date | null
    {
        const decodedToken = this._decodeToken(token);

        if ( !decodedToken || !decodedToken.hasOwnProperty('exp') )
        {
            return null;
        }

        const date = new Date(0);
        date.setUTCSeconds(decodedToken.exp);

        return date;
    }
}
